import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Eye, EyeOff, Search, Shield } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetProducts, adminDeleteProduct, adminUpdateProduct } from '../../services/api';

const CAT_COLORS = {
  suppressors: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  sme: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  'clamp-ons': 'text-green-400 bg-green-500/10 border-green-500/20',
  'muzzle-devices': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
};

export default function AdminProducts() {
  const { token } = useAdminAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    adminGetProducts(token).then(setProducts).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await adminDeleteProduct(token, id);
      setProducts(prev => prev.filter(p => p._id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const toggleStatus = async (product) => {
    const newStatus = product.status === 'active' ? 'hidden' : 'active';
    try {
      const updated = await adminUpdateProduct(token, product._id, { status: newStatus });
      setProducts(prev => prev.map(p => p._id === product._id ? updated : p));
    } catch (err) {
      alert(err.message);
    }
  };

  const filtered = products.filter(p => {
    const matchCat = catFilter === 'all' || p.category === catFilter;
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div data-testid="admin-products-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">PRODUCTS</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">{products.length} products total</p>
        </div>
        <Link
          to="/admin/products/new"
          data-testid="add-product-button"
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-inter text-sm font-medium px-4 py-2.5 transition-colors"
        >
          <Plus size={16} />New Product
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="product-search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full bg-[#1e1e1e] border border-white/10 text-white font-inter text-sm pl-9 pr-4 py-2.5 outline-none focus:border-orange-500 transition-colors placeholder-zinc-600"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'suppressors', 'sme', 'clamp-ons', 'muzzle-devices'].map(c => (
            <button
              key={c}
              data-testid={`cat-filter-${c}`}
              onClick={() => setCatFilter(c)}
              className={`font-inter text-xs px-3 py-2 border transition-colors uppercase tracking-wider ${
                catFilter === c ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-500 hover:border-white/30'
              }`}
            >
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-[#1a1a1a] border border-white/10 overflow-x-auto">
          <table className="w-full" data-testid="products-table">
            <thead>
              <tr className="border-b border-white/10">
                {['Product', 'Category', 'Price', 'Status', 'NFA', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-inter text-xs text-zinc-500 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map(p => (
                <tr key={p._id} data-testid={`product-row-${p._id}`} className="hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0f0f0f] border border-white/5 flex-shrink-0 overflow-hidden">
                        <img src={p.image} alt="" className="w-full h-full object-contain p-0.5" onError={e => { e.target.style.display = 'none'; }} />
                      </div>
                      <div>
                        <p className="font-inter text-sm text-white">{p.name}</p>
                        <p className="font-inter text-[11px] text-zinc-600">{p.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-inter text-[10px] font-semibold px-2 py-1 border uppercase tracking-wider ${CAT_COLORS[p.category] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>
                      {p.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <span className="font-bebas text-base text-white">${p.price?.toFixed(2)}</span>
                      {p.dealer_price && (
                        <span className="font-inter text-[10px] text-zinc-600 ml-1">(D: ${p.dealer_price})</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`toggle-status-${p._id}`}
                      onClick={() => toggleStatus(p)}
                      className={`flex items-center gap-1.5 font-inter text-xs px-2 py-1 border transition-colors ${
                        p.status === 'active'
                          ? 'text-green-400 bg-green-500/10 border-green-500/20 hover:bg-green-500/20'
                          : 'text-zinc-500 bg-zinc-800 border-zinc-700 hover:border-zinc-500'
                      }`}
                    >
                      {p.status === 'active' ? <><Eye size={11} />Active</> : <><EyeOff size={11} />Hidden</>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {p.nfa_item && <Shield size={14} className="text-orange-500" />}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/products/${p._id}`}
                        data-testid={`edit-product-${p._id}`}
                        className="p-1.5 text-zinc-500 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        <Pencil size={14} />
                      </Link>
                      <button
                        data-testid={`delete-product-${p._id}`}
                        onClick={() => handleDelete(p._id)}
                        className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <p className="font-inter text-sm text-zinc-600">No products found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
